import React, { useState, useEffect } from 'react';
import { Button } from '../components/UIComponents';
import { AlertCircle, ArrowRight, Check, Crown, FileText, Flame, Languages, Loader2, ShieldCheck, Sparkles, Zap } from 'lucide-react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { usePlans, useSubscription } from '../hooks/useBilling';
import { useAuth } from '../hooks/useAuth';
import { useTranslation } from 'react-i18next';
import type { Plan, BillingInterval } from '../types';
import { SEO, SITE_URL, breadcrumbJsonLd } from '../components/SEO';
import posthog from '../lib/posthog';

// Helper to format cents for display. Billing remains USD in Stripe.
function formatCurrency(cents: number, locale: string): string {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: cents % 100 === 0 ? 0 : 2,
  }).format(cents / 100);
}

function formatPrice(cents: number): string {
  return (cents / 100).toFixed(0);
}

// Helper to parse features from plan
type TranslationFn = (key: string, options?: Record<string, unknown>) => string;

function getPlanFeatures(plan: Plan, t: TranslationFn): string[] {
  const features: string[] = [];

  features.push(t('pricing.minutesPerMonth', { count: plan.monthly_minutes }));

  // Add parsed features from the plan
  if (plan.features && Array.isArray(plan.features)) {
    features.push(...plan.features);
  }

  // Add daily file limit
  if (plan.daily_file_limit === -1) {
    features.push(t('pricing.unlimitedDailyUploads'));
  } else if (plan.daily_file_limit > 0) {
    features.push(t('pricing.filesPerDay', { count: plan.daily_file_limit }));
  }

  return features;
}

const PricingShell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="min-h-screen bg-bg text-ink">{children}</div>
);

const Pricing: React.FC = () => {
  const [annual, setAnnual] = useState(true);
  const [subscribingPlanId, setSubscribingPlanId] = useState<string | null>(null);
  const [subscribeError, setSubscribeError] = useState<string | null>(null);
  const [checkoutCancelled, setCheckoutCancelled] = useState(false);
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const { isAuthenticated } = useAuth();
  const { data: plans, loading: plansLoading, error: plansError, refetch: refetchPlans } = usePlans();
  const { data: subscription, subscribe, startCheckout, openCustomerPortal } = useSubscription({ enabled: isAuthenticated });

  // Handle checkout cancelled state from URL
  useEffect(() => {
    if (searchParams.get('checkout') === 'cancelled') {
      setCheckoutCancelled(true);
      // Clean up URL
      searchParams.delete('checkout');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const hasPaidSubscription = subscription?.status === 'active' && subscription.plan_id !== 'free';

  const handleSubscribe = async (plan: Plan) => {
    setSubscribeError(null);
    setCheckoutCancelled(false);

    if (!isAuthenticated) {
      // Redirect to login with return URL
      navigate(`/login?redirect=/pricing&plan=${plan.id}`);
      return;
    }

    // If already on this plan, go to billing
    if (subscription?.plan_id === plan.id) {
      navigate('/billing');
      return;
    }

    // If user has an active paid subscription, manage plan changes in Stripe Portal
    if (hasPaidSubscription) {
      if (plan.plan_type === 'free') {
        setSubscribeError(t('pricing.downgradeUnavailable'));
        return;
      }
      try {
        setSubscribingPlanId(plan.id);
        await openCustomerPortal();
      } catch (error) {
        setSubscribeError((error as Error).message);
        setSubscribingPlanId(null);
      }
      return;
    }

    // For free plan, subscribe directly (no payment needed)
    if (plan.plan_type === 'free') {
      try {
        setSubscribingPlanId(plan.id);
        await subscribe(plan.id, 'monthly');
        navigate('/dashboard');
      } catch (error) {
        setSubscribeError((error as Error).message);
      } finally {
        setSubscribingPlanId(null);
      }
      return;
    }

    // For paid plans (new subscription or upgrading from free), use Stripe Checkout
    try {
      setSubscribingPlanId(plan.id);
      const interval: BillingInterval = annual ? 'yearly' : 'monthly';
      posthog.capture('checkout_started', {
        plan_id: plan.id,
        plan_name: plan.name,
        plan_type: plan.plan_type,
        billing_interval: interval,
        price_monthly_cents: plan.price_monthly,
        price_yearly_cents: plan.price_yearly,
      });
      await startCheckout(plan.id, interval);
      // startCheckout will redirect to Stripe, so this won't be reached
    } catch (error) {
      setSubscribeError((error as Error).message);
      setSubscribingPlanId(null);
    }
  };

  const scrollToPlans = () => {
    const target = document.getElementById('pricing-plans');
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const getButtonText = (plan: Plan): string => {
    if (subscription?.plan_id === plan.id) {
      return t('pricing.currentPlan');
    }
    if (hasPaidSubscription) {
      if (plan.plan_type === 'free') {
        return t('pricing.notAvailable');
      }
      return t('pricing.manageInStripe');
    }
    if (plan.plan_type === 'free') {
      return t('auth.startForFree');
    }
    return t('pricing.subscribe');
  };

  const isHighlighted = (plan: Plan): boolean => {
    return plan.plan_type === 'pro';
  };

  // Loading state
  if (plansLoading) {
    return (
      <PricingShell>
        <div className="shell py-24">
          <div className="flex flex-col items-center justify-center min-h-[320px] text-center">
            <Loader2 className="h-7 w-7 animate-spin text-accent" />
            <p className="t-small mt-4">{t('pricing.loadingPlans')}</p>
          </div>
        </div>
      </PricingShell>
    );
  }

  // Error state
  if (plansError) {
    return (
      <PricingShell>
        <div className="shell py-24">
          <div className="flex flex-col items-center justify-center min-h-[320px] text-center">
            <AlertCircle className="h-10 w-10 text-[#ff3b30]" />
            <p className="mt-4 text-[17px] font-semibold text-ink">{t('pricing.failedToLoad')}</p>
            <p className="t-small mt-1">{plansError}</p>
            <Button variant="secondary" className="mt-5" onClick={() => refetchPlans()}>
              {t('common.tryAgain')}
            </Button>
          </div>
        </div>
      </PricingShell>
    );
  }

  // Calculate yearly savings
  const yearlySavingsPercent = 17;
  const displayLocale = i18n.resolvedLanguage || i18n.language || 'en';
  const pricingDescription =
    'Compare OmniScribe plans for audio and video transcription, AI summaries, speaker labels, exports, team workflows, and API access.';
  const pricingJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: 'OmniScribe transcription plans',
    description: pricingDescription,
    brand: {
      '@type': 'Brand',
      name: 'OmniScribe',
    },
    offers: (plans || []).map((plan) => ({
      '@type': 'Offer',
      name: plan.name,
      price: (plan.price_monthly / 100).toFixed(0),
      priceCurrency: 'USD',
      url: `${SITE_URL}/pricing`,
      description: plan.description || `${plan.monthly_minutes} transcription minutes per month`,
      availability: 'https://schema.org/InStock',
    })),
  };

  return (
    <PricingShell>
      <SEO
        title="OmniScribe Pricing - Audio & Video Transcription Plans"
        description={pricingDescription}
        path="/pricing"
        keywords={[
          'transcription pricing',
          'audio transcription plans',
          'video transcription plans',
          'speech to text pricing',
          'AI transcription API pricing',
        ]}
        jsonLd={[
          breadcrumbJsonLd([
            { name: 'Home', path: '/' },
            { name: 'Pricing', path: '/pricing' },
          ]),
          pricingJsonLd,
        ]}
      />
      <section className="shell pt-16 text-center sm:pt-24">
        <h1 className="h-hero mx-auto max-w-[16ch] text-balance">{t('pricing.heroTitle')}</h1>
        <p className="t-lead mx-auto mt-6 max-w-[36rem] text-pretty">{t('pricing.heroDescription')}</p>

        <div className="mt-10 flex flex-col items-center gap-3">
          <div role="group" aria-label={t('pricing.plansTitle')} className="seg-group">
            <button type="button" aria-pressed={!annual} className="seg !px-5" onClick={() => setAnnual(false)}>
              {t('pricing.monthly')}
            </button>
            <button type="button" aria-pressed={annual} className="seg inline-flex items-center gap-2 !px-5" onClick={() => setAnnual(true)}>
              {t('pricing.yearly')}
              <span className="chip !px-1.5 !py-0 !text-[11px]" style={{ ['--chip-tint' as string]: 'var(--tint-mindmap)' }}>−{yearlySavingsPercent}%</span>
            </button>
          </div>
          <p className="text-[12.5px] text-ink-3">
            {t('pricing.saveWithAnnual', { percent: yearlySavingsPercent })}
            {hasPaidSubscription && <> · {t('pricing.plansDescription')}</>}
          </p>
        </div>
      </section>

      <section id="pricing-plans" className="shell scroll-mt-20 pb-10 pt-12">
        {checkoutCancelled && (
          <div className="mb-6 flex items-center gap-3 rounded-[16px] bg-[#ff9f0a]/[0.12] px-4 py-3 text-[#b25000] dark:text-[#ffb340]">
            <AlertCircle className="h-5 w-5 shrink-0" />
            <p className="text-[14px]">{t('pricing.checkoutCancelled')}</p>
          </div>
        )}

        {subscribeError && (
          <div className="mb-6 flex items-center gap-3 rounded-[16px] bg-[#ff3b30]/[0.1] px-4 py-3 text-[#d70015] dark:text-[#ff6961]">
            <AlertCircle className="h-5 w-5 shrink-0" />
            <p className="text-[14px]">{subscribeError}</p>
          </div>
        )}

        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          {plans?.map((plan) => {
            const highlighted = isHighlighted(plan);
            const features = getPlanFeatures(plan, t);
            const monthlyEquivalentCents = annual ? Math.round(plan.price_yearly / 12) : plan.price_monthly;
            const isCurrentPlan = subscription?.plan_id === plan.id;
            const isSubscribing = subscribingPlanId === plan.id;
            const isDowngradeToFree = hasPaidSubscription && plan.plan_type === 'free';

            return (
              <div
                key={plan.id}
                className={`relative flex flex-col rounded-[28px] p-7 transition-transform duration-300 ${
                  highlighted ? 'bg-raised shadow-window ring-2 ring-accent/70' : 'bg-card'
                }`}
              >
                <div className="flex items-center justify-between gap-3">
                  <h3 className="text-[17px] font-semibold tracking-[-0.01em]">{plan.name}</h3>
                  {isCurrentPlan ? (
                    <span className="chip" style={{ ['--chip-tint' as string]: 'var(--tint-mindmap)' }}>
                      <Crown className="h-3 w-3" />
                      {t('pricing.yourPlan')}
                    </span>
                  ) : highlighted ? (
                    <span className="chip">
                      <Flame className="h-3 w-3" />
                      {t('pricing.popular')}
                    </span>
                  ) : null}
                </div>

                <p className="mt-2 min-h-[2.75rem] text-[13.5px] leading-snug text-ink-2">
                  {plan.description || t('pricing.perfectFor', { type: plan.plan_type })}
                </p>

                <div className="mt-6 flex items-baseline gap-1.5">
                  <span className="figure text-[44px]">{formatCurrency(monthlyEquivalentCents, displayLocale)}</span>
                  <span className="text-[14px] text-ink-2">{t('pricing.perMonth')}</span>
                </div>
                <p className="mt-1.5 h-5 text-[12.5px] text-ink-3">
                  {annual && plan.price_monthly > 0 ? t('pricing.billedYearly', { price: formatPrice(plan.price_yearly) }) : ''}
                </p>

                <Button
                  variant={highlighted ? 'primary' : 'secondary'}
                  className={`mt-6 w-full ${highlighted ? '' : '!bg-raised hover:!bg-card-hi dark:!bg-white/[0.08]'}`}
                  onClick={() => handleSubscribe(plan)}
                  disabled={isSubscribing || isCurrentPlan || isDowngradeToFree}
                >
                  {isSubscribing ? (
                    <span className="flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      {t('pricing.processing')}
                    </span>
                  ) : (
                    getButtonText(plan)
                  )}
                </Button>

                {isDowngradeToFree && (
                  <p className="mt-2 text-center text-[12px] text-ink-3">{t('pricing.freeDowngradeUnavailable')}</p>
                )}

                <ul className="mt-7 space-y-2.5 border-t border-line pt-6">
                  {features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-start gap-2.5 text-[14px] leading-snug text-ink">
                      <Check className={`mt-0.5 h-4 w-4 shrink-0 ${highlighted ? 'text-accent' : 'text-ink-3'}`} strokeWidth={2.5} />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        <div className="t-small mt-8 flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
          {[
            { icon: Zap, label: t('pricing.featureNoSetup') },
            { icon: FileText, label: t('pricing.featureExport') },
            { icon: Languages, label: t('pricing.featureMultiLang') },
            { icon: ShieldCheck, label: t('pricing.stripeSecured') },
            { icon: Check, label: t('pricing.cancelAnytime') },
          ].map(({ icon: Icon, label }) => (
            <span key={label} className="inline-flex items-center gap-1.5">
              <Icon className="h-3.5 w-3.5 text-ink-3" />
              {label}
            </span>
          ))}
        </div>
      </section>

      <section className="shell grid items-start gap-x-20 gap-y-10 py-24 sm:py-32 lg:grid-cols-[5fr_6fr]">
        <div style={{ ['--section-tint' as string]: 'var(--tint-summary)' }}>
          <div className="num">{t('pricing.guideBadge')}</div>
          <h2 className="h-section mt-5 text-balance">{t('pricing.guideTitle')}</h2>
          <p className="t-body mt-6 text-pretty">{t('pricing.guideDescription')}</p>
        </div>
        <ol className="panel divide-y divide-line p-2">
          {[
            { title: t('pricing.guideEditableTitle'), desc: t('pricing.guideEditableDesc') },
            { title: t('pricing.guideExportTitle'), desc: t('pricing.guideExportDesc') },
            { title: t('pricing.guideUsageTitle'), desc: t('pricing.guideUsageDesc') },
          ].map((item, i) => (
            <li key={item.title} className="flex gap-4 px-5 py-5">
              <span className="figure w-6 shrink-0 text-[22px] text-tint-summary">{i + 1}</span>
              <div>
                <p className="text-[16px] font-semibold text-ink">{item.title}</p>
                <p className="mt-1 text-[14.5px] leading-relaxed text-ink-2">{item.desc}</p>
              </div>
            </li>
          ))}
        </ol>
      </section>

      <section className="bg-card">
        <div className="shell py-24 sm:py-28">
          <div className="text-center" style={{ ['--section-tint' as string]: 'var(--tint-mindmap)' }}>
            <div className="num">{t('pricing.includedBadge')}</div>
            <h2 className="h-section mx-auto mt-5 max-w-[20ch] text-balance">{t('pricing.essentialsTitle')}</h2>
            <p className="t-lead mx-auto mt-5 max-w-[36rem]">{t('pricing.essentialsDescription')}</p>
          </div>
          <div className="mt-14 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { icon: FileText, tint: 'var(--tint-export)', title: t('pricing.essentialExportTitle'), desc: t('pricing.essentialExportDesc') },
              { icon: Languages, tint: 'var(--tint-translate)', title: t('pricing.essentialTransTitle'), desc: t('pricing.essentialTransDesc') },
              { icon: Zap, tint: 'var(--tint-keypoints)', title: t('pricing.essentialFastTitle'), desc: t('pricing.essentialFastDesc') },
              { icon: Sparkles, tint: 'var(--tint-summary)', title: t('pricing.essentialAiTitle'), desc: t('pricing.essentialAiDesc') },
            ].map(({ icon: Icon, tint, title, desc }) => (
              <div key={title} className="rounded-[22px] bg-raised p-6">
                <span className="grid h-10 w-10 place-items-center rounded-[12px]" style={{ background: `rgb(${tint} / 0.13)`, color: `rgb(${tint})` }}>
                  <Icon className="h-5 w-5" />
                </span>
                <h3 className="mt-5 text-[16px] font-semibold text-ink">{title}</h3>
                <p className="mt-1.5 text-[14px] leading-relaxed text-ink-2">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="faq-refund" className="shell scroll-mt-20 py-24 sm:py-32">
        <div className="grid gap-12 lg:grid-cols-[4fr_7fr]">
          <div>
            <div className="num" style={{ ['--section-tint' as string]: 'var(--tint-keypoints)' }}>{t('pricing.faqBadge')}</div>
            <h2 className="h-section mt-5 text-balance">{t('pricing.faqTitle')}</h2>
            <p className="t-body mt-5">{t('pricing.faqDescription')}</p>
          </div>
          <div className="border-y border-line py-6">
            <h3 className="text-[19px] font-semibold tracking-[-0.01em] text-ink">{t('pricing.refundFaqQuestion')}</h3>
            <div className="mt-4 space-y-3 text-[15.5px] leading-relaxed text-ink-2">
              <p>{t('pricing.refundFaqIntro')}</p>
              <p>{t('pricing.refundFaqMonthly')}</p>
              <p>{t('pricing.refundFaqAnnual')}</p>
              <p>
                {t('pricing.refundFaqContact')}{' '}
                <a href="mailto:support@omniscribe.ai" className="font-medium text-accent hover:underline underline-offset-4">
                  support@omniscribe.ai
                </a>
                .
              </p>
              <Link to="/refund-policy" className="inline-flex items-center gap-1 pt-1 text-[15px] font-medium text-accent hover:underline underline-offset-4">
                {t('pricing.readRefundPolicy')}
                <ArrowRight className="h-4 w-4 rtl:rotate-180" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="shell pb-28 text-center">
        <h2 className="h-section mx-auto max-w-[18ch] text-balance">{t('pricing.plansTitle')}</h2>
        <div className="mt-9 flex flex-wrap items-center justify-center gap-3">
          <button type="button" className="btn" onClick={() => (isAuthenticated ? scrollToPlans() : navigate('/register'))}>
            {t('pricing.startFree')}
            <ArrowRight className="h-4 w-4 rtl:rotate-180" />
          </button>
          <button type="button" className="btn btn-quiet" onClick={scrollToPlans}>{t('pricing.seePlans')}</button>
        </div>
      </section>
    </PricingShell>
  );
};

export default Pricing;
